/**
 * The only service module allowed to call `/auth/*` directly — every
 * other service goes through `core/api/http-client`'s shared instance
 * for authenticated calls. `login` is real and fully wired against the
 * real, certified `POST /auth/login` endpoint. `logout` is
 * deliberately client-side only (see its own comment) — there is no
 * `POST /auth/logout` on the real backend to call
 * (`FRONTEND-BACKEND-INTEGRATION.md` §3).
 */
import { httpClient, unwrap, API_ENDPOINTS } from '@core/api';
import type { LoginRequest, LoginResponse, LoginResult, MfaVerifyRequest } from '@app-types/auth.types';
import type { ApiSuccessResponse } from '@app-types/api.types';

export async function login(request: LoginRequest): Promise<LoginResponse> {
  return unwrap(httpClient.post<ApiSuccessResponse<LoginResponse>>(API_ENDPOINTS.auth.login, request));
}

// Phase S1 — completes login after a challenge. The trusted-device
// cookie (if "remember this device" was requested) is set by the
// browser automatically from the response's Set-Cookie header — same-
// origin, no special client config needed.
export async function verifyMfa(request: MfaVerifyRequest): Promise<LoginResult> {
  return unwrap(httpClient.post<ApiSuccessResponse<LoginResult>>(API_ENDPOINTS.auth.mfaVerify, request));
}

// No logout() here, deliberately: there is no `POST /auth/logout` on the real
// backend (`FRONTEND-BACKEND-INTEGRATION.md` §3) — this is a stateless-JWT
// design with no server-side session to invalidate, so logout is entirely a
// client-side concern (`hooks/useAuth.ts`'s own `logout`, which clears the
// persisted session and lets route guards redirect). Not a workaround for a
// missing endpoint; this is the correct shape for how this backend actually
// works.
